import { useCallback, useMemo } from "react"

import createStore from "@Stores/base-factories"
import { hookIntegrationList, hookPostList, hookRewardList, hookRewardStepList, hookSiteList, hookMetaFieldList } from "@Stores/base-factories/store-list"
import hookSiteSelectedFactory from "@Stores/base-factories/store-site-selection"

const useIntegrationFactory = ({ fetch, siteSelected: { id: siteId, key: siteKey }, collection: { collectionURL }, collections: { collectionsURL } }) => {
  const duplicateIntegration = useCallback(async (object, params, targetSiteId) => {
    object.id = null;
    if (targetSiteId) {
      object.siteID = targetSiteId;
      object.site_id = targetSiteId;
      object.siteId = targetSiteId;
    }
    return await fetch({
      method: "post",
      url: `${collectionURL}/`,
      data: object,
      params,
    })
  }, [collectionURL, fetch])

  const getGoogleAuthURL = useCallback(async () => {
    const { authURL } = await fetch({
      method: "post",
      url: `${collectionURL}/google-auth-url`,
    })
    return authURL
  }, [collectionURL, fetch])

  const getGoogleCustomers = useCallback(async credentials => await fetch({
    method: "post",
    url: `${collectionURL}/google-customers`,
    data: { credentials },
  }), [collectionURL, fetch])

  const getGoogleConversionActions = useCallback(async (credentials, customerId) => await fetch({
    method: "post",
    url: `${collectionURL}/google-conversion-actions`,
    data: { credentials, customerId },
  }), [collectionURL, fetch])

  const sendUnsubscribeEmail = useCallback(async payload => await fetch({
    method: "post",
    url: `${collectionsURL}/sendunsubscribeemail`,
    data: payload,
    params: { [siteKey]: siteId },
  }), [collectionsURL, fetch, siteKey, siteId])

  const unsubscribe = useCallback(async ({ integrationId, emails }) => await fetch({
    method: "post",
    url: `${collectionsURL}/unsubscribe`,
    data: { integrationId, emails },
    params: { [siteKey]: siteId },
  }), [collectionsURL, fetch, siteKey, siteId])

  const getInProgress = useCallback(async () => await fetch({
    url: `${collectionsURL}/inprogress`,
    params: { [siteKey]: siteId },
  }), [collectionsURL, fetch, siteKey, siteId])

  const getSweepFinished = useCallback(async sweepId => await fetch({
    url: `${collectionsURL}/sweeps/finished`,
    params: { [siteKey]: siteId, sweepId },
  }), [collectionsURL, fetch, siteKey, siteId])

  const downloadSubscriptionFile = useCallback(async (integrationId, contestId) => {
    const response = await fetch({
      url: `${collectionURL}/${integrationId}/sweeps/contest/${contestId}/download`,
      responseType: 'blob',
      params: { [siteKey]: siteId },
    })
    const type = response.headers['content-type']
    const filename = response.headers['content-filename']
    const blob = new Blob([response.data], { type: type, encoding: 'UTF-8' })
    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.download = filename
    link.click()
  }, [collectionURL, fetch, siteKey, siteId])

  return useMemo(() => ({
    duplicateIntegration, getGoogleAuthURL, getGoogleCustomers, getGoogleConversionActions,
    sendUnsubscribeEmail, unsubscribe, getInProgress, getSweepFinished, downloadSubscriptionFile,
  }), [duplicateIntegration, getGoogleAuthURL, getGoogleCustomers, getGoogleConversionActions, sendUnsubscribeEmail, unsubscribe, getInProgress, getSweepFinished, downloadSubscriptionFile])
}

const { Provider, useStore } = createStore()
  .extend(hookSiteSelectedFactory('site_id'))
  .extendHookCollectionFactory('integration')
  .extendHookCollectionsFactory('integrations')
  .extend(useIntegrationFactory)
  .extend(hookIntegrationList())
  .extend(hookPostList())
  .extend(hookSiteList())
  .extend(hookRewardList())
  .extend(hookRewardStepList("rewardSteps"))
  .extend(hookMetaFieldList())
  .factory()

export {
  Provider as IntegrationProvider,
  useStore as useIntegrationStore,
}
